import i18n from '@i18next/i18n';
import { ToolListType } from '@src/framework/editor/src/js/constants/toolbar-data';
import config, { EnterPriseType } from '@src/js/service/enterprise/temp-config';
import { CreateChartTypeName, PresetChartType } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { TENANT_CONFIG } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import DocsConfig from '@tencent/docs-config';
import PC_FREE from '@tencent/docs-design-resources/pc/svg/pc_free_tag.svg';
import ua from '@tencent/docs-user-agent';
import { ActionType } from './common-action-type';
import { getCacConfigByKey } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';

/**
 * 右键菜单单一配置项
 * @typedef {{
 *      id: string,
 *      title?: string,
 *      shortcut?: string,
 *      disabled?: boolean,
 *      disabledContent?: string
 * }} RightMenuItem
 */

// 是否开启视频设置封面功能
const ENABLE_SET_VIDEO_COVER = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.setVideoCover) ?? false;
// 是否需要屏蔽下载文件入口(比如：桌面端本地编辑场景)
const ENABLE_DOWNLOAD_MEDIA = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.downloadMedia) ?? true;
// 是否支持裁剪视频
const ENABLE_TAILOR_VIDEO = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.tailorVideo);
// 开关控制是否开启分页导入在线文档功能
// 企微项目暂时关闭此功能
const ENABLE_IMPORT_ONLINE_SLIDE = !config.supportEnterprise(EnterPriseType.ALL);
// 插件能力
const ENABLE_ADDON = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.addonUnofficial) ?? true;
// 快捷美化
const ENABLE_QUICKTOOL = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.quickTool) ?? true;
// 图片提取文字
const ENABLE_OCR = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.ocr) ?? true;

//  ------------右键菜单（跟topMenu会有id重复的功能）-------------------------

// 是否需要支持导入幻灯片入口,包括拖拽导入(默认支持，特殊场景不支持 比如：桌面端本地编辑场景)
export const ENABLE_IMPORT_FILE = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.importFile) ?? true;
// 幻灯片可复制的右键菜单
export const OnlyCopyRightMenuItems: ToolListType[] = [
    {
        id: ActionType.COPY,
        title: i18n.t('复制'),
        shortcut: 'Cmd+C',
        disabledContent: i18n.t('权限设置无法复制内容'),
    },
];

export const OnlyDownloadOriginalImage = !ua.isWxMiniProgram ? [
    {
        id: ActionType.DOWNLOAD_PICTURE,
        title: i18n.t('下载原图'),
        disabledContent: i18n.t('权限设置无法导出内容'),
    },
] : [];

export const AddonItem: ToolListType = {
    id: ActionType.ADDON,
    title: i18n.t('插件'),
};

const QuickToolItem = {
    id: ActionType.QUICK_TOOL,
    title: i18n.t('快捷美化'),
    children: [
        { id: ActionType.COLOR_HANDLER, title: i18n.t('颜色处理') },
        { id: ActionType.CHANGE_THEME, title: i18n.t('一键换色'), appendIcon: PC_FREE },
        { id: 'divider-menu-item' },
        { id: ActionType.FONT_HANDLER, title: i18n.t('字体处理') },
        { id: ActionType.OPEN_UNIFORM_FONT, title: i18n.t('统一字体') },
        // { id: ActionType.OPEN_REPLACE_FONT, title: i18n.t('替换字体') },
        { id: 'divider-menu-item' },
        { id: ActionType.PIC_HANDLER, title: i18n.t('图片处理') },
        { id: ActionType.OPEN_MUTI_PICTURE_SIZE_SETTING, title: i18n.t('批量设置图片尺寸') },
        { id: ActionType.OPEN_MUTI_PICTURE_POS_SETTING, title: i18n.t('批量图片布局') },
        !(ENV.IS_PC_LOCAL_EDIT || ua.isWxMiniProgram) && { id: ActionType.OPEN_MUTI_PICTURE_EXPORT, title: i18n.t('批量图片下载') },
        { id: ActionType.OPEN_IMAGES_JOINT, title: i18n.t('图片拼接') },
        { id: ActionType.VIP_LINK_TEXT, style: { height: '30px', padding: '0' } },
    ].filter(Boolean),
};

// 母版编辑模式下的右键菜单
export const MasterRightMenuItems = [
    {
        id: ActionType.PASTE,
        title: i18n.t('粘贴'),
        shortcut: 'Cmd+V',
    },
    {
        id: ActionType.SELECT_ALL,
        title: i18n.t('全选'),
        shortcut: 'Cmd+A',
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    {
        id: ActionType.SET_BG_FORMAT,
        title: i18n.t('设置背景格式'),
    },
    {
        id: ActionType.DISPLAY_TEXT_RULER,
        title: i18n.t('标尺'),
    },
    {
        id: ActionType.GRID_AND_GUIDES,
        title: i18n.t('网格和参考线'),
        children: [
            { id: ActionType.ADD_VERT_GUIDE, title: i18n.t('添加垂直参考线') },
            { id: ActionType.ADD_HORZ_GUIDE, title: i18n.t('添加水平参考线') },
            { id: 'divider-menu-item' },
            { id: ActionType.TOGGLE_SNAP_LINE, title: i18n.t('智能吸附') },
            { id: ActionType.DISPLAY_GUIDES, title: i18n.t('参考线') },
            { id: ActionType.DISPLAY_GRID_LINE, title: i18n.t('网格线') },
            { id: 'divider-menu-item' },
            { id: ActionType.GRID_LINE_ALIGN, title: i18n.t('对齐网格') },
            { id: ActionType.GRID_DIALOG, title: i18n.t('网格选项') },
        ],
    },
    {
        id: ActionType.SHOW_RATIO,
        title: i18n.t('显示比例'),
    },
    !ua.isTencentMeeting && {
        id: ActionType.SLIDE_PLAY,
        title: i18n.t('从当前页演示'),
    },
    ...withDivider([
        ENABLE_QUICKTOOL && QuickToolItem,
    ]),
].filter(Boolean);

export const CommentRightMenuItems = getCacConfigByKey('feature.comment', false) ? [
    {
        id: ActionType.INSERT_COMMENT,
        title: i18n.t('批注'),
        shortcut: 'Option+Cmd+M',
    },
    { id: ActionType.DIVIDER },
] : [];

// 空白处右键显示菜单
export const BlankRightMenuItems = [
    // {
    //     id: ActionType.COPY,
    //     title: '复制',
    //     shortcut: 'Cmd+C',
    // },
    {
        id: ActionType.PASTE,
        title: i18n.t('粘贴'),
        shortcut: 'Cmd+V',
    },
    {
        id: ActionType.SELECT_ALL,
        title: i18n.t('全选'),
        shortcut: 'Cmd+A',
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    {
        id: ActionType.CREATE_SLIDE,
        title: i18n.t('新建页面'),
        shortcut: 'Ctrl+M',
    },
    {
        id: ActionType.COPY_SLIDE,
        title: i18n.t('复制页面'),
    },
    {
        id: ActionType.HIDE_SLIDE,
        title: i18n.t('隐藏页面'),
    },
    {
        id: ActionType.DELETE_SLIDE,
        title: i18n.t('删除页面'),
    },
    {
        id: ActionType.ADD_SECTION,
        title: i18n.t('添加节'),
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    {
        id: ActionType.SET_BG_FORMAT,
        title: i18n.t('设置背景格式'),
    },
    {
        id: ActionType.DISPLAY_TEXT_RULER,
        title: i18n.t('标尺'),
    },
    {
        id: ActionType.GRID_AND_GUIDES,
        title: i18n.t('网格和参考线'),
        children: [
            { id: ActionType.ADD_VERT_GUIDE, title: i18n.t('添加垂直参考线') },
            { id: ActionType.ADD_HORZ_GUIDE, title: i18n.t('添加水平参考线') },
            { id: 'divider-menu-item' },
            { id: ActionType.TOGGLE_SNAP_LINE, title: i18n.t('智能吸附') },
            { id: ActionType.DISPLAY_GUIDES, title: i18n.t('参考线') },
            { id: ActionType.DISPLAY_GRID_LINE, title: i18n.t('网格线') },
            { id: 'divider-menu-item' },
            { id: ActionType.GRID_LINE_ALIGN, title: i18n.t('对齐网格') },
            { id: ActionType.GRID_DIALOG, title: i18n.t('网格选项') },
        ],
    },
    {
        id: ActionType.SHOW_RATIO,
        title: i18n.t('显示比例'),
    },
    !ua.isTencentMeeting && {
        id: ActionType.SLIDE_PLAY,
        title: i18n.t('从当前页演示'),
    },
    ...CommentRightMenuItems.slice().reverse(),
    ...withDivider([
        ENABLE_QUICKTOOL && QuickToolItem,
        ENABLE_ADDON && AddonItem,
    ]),
].filter(Boolean);

//  右键菜单通用操作
export const CommonRightMenuItems: ToolListType[] = [
    {
        id: ActionType.CUT,
        title: i18n.t('剪切'),
        shortcut: 'Cmd+X',
    },
    ...OnlyCopyRightMenuItems,
    {
        id: ActionType.PASTE,
        title: i18n.t('粘贴'),
        shortcut: 'Cmd+V',
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    {
        id: ActionType.MOVE_ITEM_UP,
        title: i18n.t('上移一层'),
    },
    {
        id: ActionType.ROOT_UPPER,
        title: i18n.t('置顶'),
    },
    {
        id: ActionType.MOVE_ITEM_DOWN,
        title: i18n.t('下移一层'),
    },
    {
        id: ActionType.BASE_DOWN,
        title: i18n.t('置底'),
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    {
        id: ActionType.ALIGN,
        title: i18n.t('对齐'),
        children: [
            { id: ActionType.ALIGN_HORIZONTALLT_LEFT, title: i18n.t('左对齐') },
            { id: ActionType.ALIGN_HORIZONTALLT_CENTER, title: i18n.t('居中') },
            { id: ActionType.ALIGN_HORIZONTALLT_RIGHT, title: i18n.t('右对齐') },
            { id: ActionType.DIVIDER },  // 分割线，所有分割线，都是这个ID
            { id: ActionType.ALIGN_VERTICALLY_TOP, title: i18n.t('顶部对齐') },
            { id: ActionType.ALIGN_VERTICALLY_CENTER, title: i18n.t('垂直居中') },
            { id: ActionType.ALIGN_VERTICALLY_BOTTOM, title: i18n.t('底部对齐') },
            { id: ActionType.DIVIDER },  // 分割线，所有分割线，都是这个ID
            { id: ActionType.ALIGN_DISTRIBUTE_HORIZONTALLT, title: i18n.t('横向分散') },
            { id: ActionType.ALIGN_DISTRIBUTE_VERTICALLY, title: i18n.t('纵向分散') },
        ],
    },
    {
        id: ActionType.INSERT_ANIMATION,
        title: i18n.t('编辑动画'),
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
];

export const NoteRightMenuItems = [
    {
        id: ActionType.CUT,
        title: i18n.t('剪切'),
        shortcut: 'Cmd+X',
    },
    ...OnlyCopyRightMenuItems,
    {
        id: ActionType.PASTE,
        title: i18n.t('粘贴'),
        shortcut: 'Cmd+V',
    },
];

const CommonTextViewRightMenuItems = [
    {
        id: ActionType.LINK,
        title: i18n.t('插入链接'),
        shortcut: 'Cmd+K',
    },
    {
        id: ActionType.ENTER_EDITOR,
        title: i18n.t('编辑文字'),
    },
    // {
    //     id: ActionType.EDIT_TOP_POINT,
    //     title: '编辑顶点',
    // },
    // {
    //     id: ActionType.INTELLIGENT_TRANSLATION,
    //     title: '翻译',
    // },
    {
        id: ActionType.SHAPE_FORMAT,
        title: i18n.t('设置格式'),
    },
    ...withDivider([
        ENABLE_QUICKTOOL && QuickToolItem,
    ]),
].filter(Boolean);

//  右键菜单选中文本框操作
export const TextViewRightMenuItems = CommonRightMenuItems.concat(CommentRightMenuItems, CommonTextViewRightMenuItems);

export const GuidesSettingMenuItems = [
    { id: ActionType.ADD_VERT_GUIDE, title: i18n.t('添加垂直参考线') },
    { id: ActionType.ADD_HORZ_GUIDE, title: i18n.t('添加水平参考线') },
    {
        id: ActionType.SET_GUIDE_COLOR,
        title: i18n.t('颜色'),
        children: [
            { id: ActionType.GUIDE_COLOR_PANEL, title: i18n.t('色板') },
        ],
    },
    { id: ActionType.DELETE_GUIDE, title: i18n.t('删除') },
];

export const ThemeSettingMenuItems = [
    { id: ActionType.THEME_EDIT, title: i18n.t('编辑'), style: { width: 168 } },
    { id: ActionType.THEME_DELETE, title: i18n.t('删除'), style: { width: 168 } },
];

// 可编辑状态下
export const TextViewEditRightMenuItems = CommonRightMenuItems.concat({
    id: ActionType.PARAGRAPH_MORE,
    title: i18n.t('段落'),
}, CommonTextViewRightMenuItems);

// 母版编辑下的元素右键菜单
export const MasterAndLayoutPlaceHolderTextViewRightMeuItems = CommonRightMenuItems.concat([
    {
        id: ActionType.ENTER_EDITOR,
        title: i18n.t('编辑文字'),
    },
    {
        id: ActionType.SHAPE_FORMAT,
        title: i18n.t('设置格式'),
    },
    ...withDivider([
        ENABLE_QUICKTOOL && QuickToolItem,
    ]),
].filter(Boolean));

//  右键菜单选中连接线操作
export const ConnectorRightMenuItems = CommonRightMenuItems.concat(CommentRightMenuItems, [
    {
        id: ActionType.SHAPE_FORMAT,
        title: i18n.t('设置形状格式'),
    },
]);

// 裁剪菜单项(图片视频通用)
const TailorPicItem = {
    id: ActionType.TAILOR_PIC,
    title: i18n.t('裁剪为'),
    children: [
        { id: ActionType.TAILOR_PIC_NORMAL, title: i18n.t('裁剪') },
        {
            id: ActionType.TAILOR_PIC_SHAPE, title: i18n.t('裁剪为形状'), children: [
                { id: ActionType.TAILOR_PIC_SELECT_SHAPE, title: i18n.t('形状选择框') },
            ],
        },
        {
            id: ActionType.TAILOR_PIC_WH, title: i18n.t('裁剪为纵横比'),
            children: [
                { id: ActionType.TAILOR_PIC_WH_1_1, title: '1:1' },
                { id: ActionType.DIVIDER },
                { id: ActionType.TAILOR_PIC_WH_2_3, title: '2:3' },
                { id: ActionType.TAILOR_PIC_WH_3_4, title: '3:4' },
                { id: ActionType.TAILOR_PIC_WH_3_5, title: '3:5' },
                { id: ActionType.TAILOR_PIC_WH_4_5, title: '4:5' },
                { id: ActionType.DIVIDER },
                { id: ActionType.TAILOR_PIC_WH_3_2, title: '3:2' },
                { id: ActionType.TAILOR_PIC_WH_4_3, title: '4:3' },
                { id: ActionType.TAILOR_PIC_WH_5_3, title: '5:3' },
                { id: ActionType.TAILOR_PIC_WH_5_4, title: '5:4' },
                { id: ActionType.TAILOR_PIC_WH_16_9, title: '16:9' },
                { id: ActionType.TAILOR_PIC_WH_16_10, title: '16:10' },
            ],
        },
    ],
};

const TailorPicAndQuickToolItems = [
    TailorPicItem,
    {
        id: ActionType.SHAPE_FORMAT,
        title: i18n.t('设置格式'),
    },
    ...withDivider([
        ENABLE_QUICKTOOL && QuickToolItem,
    ]),
];

const ImageFillTextViewRightMenuItems = [
    {
        id: ActionType.LINK,
        title: i18n.t('插入链接'),
        shortcut: 'Cmd+K',
    },
    {
        id: ActionType.ENTER_EDITOR,
        title: i18n.t('编辑文字'),
    },
    ...TailorPicAndQuickToolItems,
].filter(Boolean);
// 右键选中图片填充元素
export const TextViewImageFillRightMenuItems = CommonRightMenuItems.concat(ImageFillTextViewRightMenuItems);

export const MediaRightMenuItems = [
    {
        id: ActionType.DIVIDER,
    },
    ...CommentRightMenuItems,
    ENABLE_TAILOR_VIDEO && TailorPicItem,
    ENABLE_DOWNLOAD_MEDIA && {
        id: ActionType.DOWNLOAD_ATTACHMENT,
        title: i18n.t('下载文件'),
    },
    ENABLE_SET_VIDEO_COVER && {
        id: ActionType.SET_COVER,
        title: i18n.t('设置封面'),
        children: [
            { id: ActionType.FILE_COVER, title: i18n.t('从文件选择') },
            { id: ActionType.VIDEO_COVER, title: i18n.t('当前框架') },
            { id: ActionType.RESET_COVER, title: i18n.t('重置') },
        ],
    },
].filter(Boolean);

export const AttachmentRightMenuItem = [
    ...CommentRightMenuItems,
    {
        id: ActionType.DOWNLOAD_ATTACHMENT,
        title: i18n.t('下载文件'),
    },
];

// 图片类附件（图片附件、音视频附件预览态）右键菜单
export const ImageAttachmentRightMenuItems = CommonRightMenuItems.concat(AttachmentRightMenuItem);

// 形状类附件（文档附件、各种其他附件的文字链态）右键菜单
export const ShapeAttachmentRightMenuItems = CommonRightMenuItems.concat(AttachmentRightMenuItem);

//  右键菜单选中图片操作
export const PicRightMenuItems = CommonRightMenuItems.concat([
    {
        id: ActionType.LINK,
        title: i18n.t('插入链接'),
        shortcut: 'Cmd+K',
    },
    TailorPicItem,
    {
        id: ActionType.PIC_FORMAT,
        title: i18n.t('设置图片格式'),
    },
    ENABLE_OCR && {
        id: ActionType.OCR_PICTURE,
        title: i18n.t('提取图片文字'),
    },
    ...OnlyDownloadOriginalImage,
    ...CommentRightMenuItems.slice().reverse(),
    ...withDivider([
        ENABLE_QUICKTOOL && QuickToolItem,
        ENABLE_ADDON && AddonItem,
    ]),
].filter(Boolean));

//  右键菜单选中图表操作
export const ChartRightMenuItems = CommonRightMenuItems.slice(0, -1).concat([
    { id: ActionType.DIVIDER },
    ...CommentRightMenuItems,
    {
        id: ActionType.MODIFY_CHART_LAYOUT,
        title: i18n.t('更改图表布局'),
        children: [
            { id: ActionType.MODIFY_CHART_LAYOUT_CONTENT, title: i18n.t('布局选择器') },
        ],
    },
    {
        id: ActionType.MODIFY_CHART_TYPE,
        title: i18n.t('更改图表类型'),
        children: [
            {
                id: ActionType.MODIFY_CHART_TYPE_COLUMN,
                title: i18n.t('柱状图'),
                children: [
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_COLUMN_CLUSTERED,
                        title: CreateChartTypeName[PresetChartType.COLUMN_CLUSTERED],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_COLUMN_STACKED,
                        title: CreateChartTypeName[PresetChartType.COLUMN_STACKED],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_COLUMN_PERCENT_STACKED,
                        title: CreateChartTypeName[PresetChartType.COLUMN_PERCENT_STACKED],
                    },
                ],
            },
            {
                id: ActionType.MODIFY_CHART_TYPE_LINE,
                title: i18n.t('折线图'),
                children: [
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_LINE_STANDARD,
                        title: CreateChartTypeName[PresetChartType.LINE_STANDARD],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_LINE_STACKED,
                        title: CreateChartTypeName[PresetChartType.LINE_STACKED],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_LINE_PERCENT_STACKED,
                        title: CreateChartTypeName[PresetChartType.LINE_PERCENT_STACKED],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_LINE_STANDARD_WITH_POINT,
                        title: CreateChartTypeName[PresetChartType.LINE_STANDARD_WITH_POINT],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_LINE_STACKED_WITH_POINT,
                        title: CreateChartTypeName[PresetChartType.LINE_STACKED_WITH_POINT],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_LINE_PERCENT_STACKED_WITH_POINT,
                        title: CreateChartTypeName[PresetChartType.LINE_PERCENT_STACKED_WITH_POINT],
                    },
                ],
            },
            {
                id: ActionType.MODIFY_CHART_TYPE_BAR,
                title: i18n.t('条形图'),
                children: [
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_BAR_CLUSTERED,
                        title: CreateChartTypeName[PresetChartType.BAR_CLUSTERED],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_BAR_STACKED,
                        title: CreateChartTypeName[PresetChartType.BAR_STACKED],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_BAR_PERCENT_STACKED,
                        title: CreateChartTypeName[PresetChartType.BAR_PERCENT_STACKED],
                    },
                ],
            },
            {
                id: ActionType.MODIFY_CHART_TYPE_PIE,
                title: i18n.t('饼图'),
                children: [
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_PIE,
                        title: CreateChartTypeName[PresetChartType.PIE],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_OF_PIE,
                        title: CreateChartTypeName[PresetChartType.OF_PIE],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_OF_PIE_BAR,
                        title: CreateChartTypeName[PresetChartType.OF_PIE_BAR],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_DOUGHNUT,
                        title: CreateChartTypeName[PresetChartType.DOUGHNUT],
                    },
                ],
            },
            {
                id: ActionType.MODIFY_CHART_TYPE_AREA,
                title: i18n.t('面积图'),
                children: [
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_AREA,
                        title: CreateChartTypeName[PresetChartType.AREA],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_AREA_STACKED,
                        title: CreateChartTypeName[PresetChartType.AREA_STACKED],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_AREA_PERCENT_STACKED,
                        title: CreateChartTypeName[PresetChartType.AREA_PERCENT_STACKED],
                    },
                ],
            },
            {
                id: ActionType.MODIFY_CHART_TYPE_RADAR,
                title: i18n.t('雷达图'),
                children: [
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_RADAR,
                        title: CreateChartTypeName[PresetChartType.RADAR],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_RADAR_WITH_POINT,
                        title: CreateChartTypeName[PresetChartType.RADAR_WITH_POINT],
                    },
                    {
                        id: ActionType.MODIFY_CHART_TYPE_TO_RADAR_WITH_FILL,
                        title: CreateChartTypeName[PresetChartType.RADAR_WITH_FILL],
                    },
                ],
            },
        ],
    },
    {
        id: ActionType.EDIT_CHART_DATA,
        title: i18n.t('数据编辑'),
    },
    {
        id: ActionType.CHART_FORMAT,
        title: i18n.t('设置格式'),
    },
]);

export const ChartFallbackMenuItems = CommonRightMenuItems.slice(0, -1).concat([
    { id: ActionType.DIVIDER },
    {
        id: ActionType.CHART_FORMAT,
        title: i18n.t('设置格式'),
    },
]);


export const SmartArtMenuItems = CommonRightMenuItems.concat([
    {
        id: ActionType.SHAPE_FORMAT,
        title: i18n.t('设置格式'),
    },
    {
        id: ActionType.CONVERT_TO_GROUP,
        title: i18n.t('转成组合'),
    },

]);


//  右键菜单选中表格操作
export const TableRightMenuItems = CommonRightMenuItems.concat([
    {
        id: ActionType.MERGE_CELL,
        title: i18n.t('合并单元格'),
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    {
        id: ActionType.INSERT_ROW_COL,
        title: i18n.t('插入行列'),
        children: [
            {
                id: ActionType.INSERT_ROW_TOP,
                title: i18n.t('上方插入行'),
            },
            {
                id: ActionType.INSERT_ROW_BOTTOM,
                title: i18n.t('下方插入行'),
            },
            {
                id: ActionType.INSERT_COL_LEFT,
                title: i18n.t('左侧插入列'),
            },
            {
                id: ActionType.INSERT_COL_RIGHT,
                title: i18n.t('右侧插入列'),
            },
        ],
    },
    {
        id: ActionType.DELETE_ROW_COL,
        title: i18n.t('删除行列'),
        children: [
            {
                id: ActionType.DELETE_ROW,
                title: i18n.t('删除所在行'),
            },
            {
                id: ActionType.DELETE_COL,
                title: i18n.t('删除所在列'),
            },
        ],
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    // {
    //     id: ActionType.MERGE_CELL,
    //     title: '合并、取消合并单元格'
    // },
    // {
    //     id: ActionType.CLEAR_FORMAT,
    //     title: '清除格式',
    // },
    {
        id: ActionType.DELETE_TABLE,
        title: i18n.t('删除表格'),
    },
    // {
    //     id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    // },
    // {
    //     id: ActionType.INTELLIGENT_TRANSLATION,
    //     title: '翻译',
    // },
    {
        id: ActionType.SHAPE_FORMAT,
        title: i18n.t('设置形状格式'),
    },
    ...CommentRightMenuItems.slice().reverse(),
    ...withDivider([
        ENABLE_QUICKTOOL && QuickToolItem,
    ]),
].filter(Boolean));

//  右键菜单选中文字框操作
// export const TextRightMenuItems = [
//     {
//         id: ActionType.INSERT_LINK,
//         title: '插入链接',
//     },
//     {
//         id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
//     },
//     {
//         id: ActionType.CUT,
//         title: '剪切',
//         shortcut: 'Cmd+X',
//     },
//     {
//         id: ActionType.COPY,
//         title: '复制',
//         shortcut: 'Cmd+C',
//     },
//     {
//         id: ActionType.PASTE,
//         title: '粘贴',
//         shortcut: 'Cmd+V',
//     },
//     {
//         id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
//     },
//     {
//         id: ActionType.COMMENT,
//         title: '评论',
//     },
// ];

const GroupItems = [{
    id: ActionType.GROUP, // 组合/取消组合 一级入口
    title: i18n.t('组合'),
    children: [
        { id: ActionType.ADDGROUP, title: i18n.t('组合') },
        { id: ActionType.REMOVEGROUP, title: i18n.t('取消组合') },
    ],
}];

//  多选菜单
export const GroupRightMenuItems = CommonRightMenuItems.concat([
    ...CommentRightMenuItems,
    ...GroupItems,
    {
        id: ActionType.SHAPE_FORMAT,
        title: i18n.t('设置格式'),
    },
]);

export const GroupIncludeImageRightMenuItems = CommonRightMenuItems.concat([
    ...CommentRightMenuItems,
    ...GroupItems,
    ...TailorPicAndQuickToolItems,
].filter(Boolean));

export const MultiRightMenuItems = CommonRightMenuItems.concat([
    ...GroupItems,
    {
        id: ActionType.SHAPE_FORMAT,
        title: i18n.t('设置格式'),
    },
]);

export const MultiIncludeImageRightMenuItems = CommonRightMenuItems.concat([
    ...GroupItems,
    ...TailorPicAndQuickToolItems,
].filter(Boolean));

export const SlideRightMenuItems = [
    {
        id: ActionType.CUT,
        title: i18n.t('剪切'),
        shortcut: 'Cmd+X',
    },
    ...OnlyCopyRightMenuItems,
    {
        id: ActionType.PASTE,
        title: i18n.t('粘贴'),
        shortcut: 'Cmd+V',
    },
    {
        id: ActionType.SELECT_ALL,
        title: i18n.t('全选'),
        shortcut: 'Cmd+A',
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    {
        id: ActionType.CREATE_SLIDE,
        title: i18n.t('新建页面'),
        shortcut: 'Ctrl+M',  // Mac上cmd+m是系统快捷键，这里统一处理成ctrl+m
    },
    {
        id: ActionType.COPY_SLIDE,
        title: i18n.t('复制页面'),
    },
    {
        id: ActionType.HIDE_SLIDE,
        title: i18n.t('隐藏页面'),
    },
    {
        id: ActionType.DELETE_SLIDE,
        title: i18n.t('删除页面'),
    },
    {
        id: ActionType.ADD_SECTION,
        title: i18n.t('添加节'),
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    // {
    //     id: ActionType.SHOW_RULE,
    //     title: '显示标尺',
    // },
    {
        id: ActionType.SET_BG_FORMAT,
        title: i18n.t('设置背景格式'),
    },
    {
        id: ActionType.SHOW_RATIO,
        title: i18n.t('显示比例'),
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    ...CommentRightMenuItems,
    ENABLE_IMPORT_FILE && {
        id: ActionType.IMPORT_SLIDE,
        title: i18n.t('导入幻灯片'),
        children: [
            { id: ActionType.IMPORT_LOCAL_SLIDE, title: i18n.t('从本地文档') },
            ENABLE_IMPORT_ONLINE_SLIDE && { id: ActionType.IMPORT_ONLINE_SLIDE, title: TENANT_CONFIG?.TENANT_DOC_NAME ? `从${TENANT_CONFIG?.TENANT_DOC_NAME}` : i18n.t('从腾讯文档') },
        ].filter(Boolean),
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    !ua.isTencentMeeting && {
        id: ActionType.SLIDE_PLAY,
        title: i18n.t('从当前页演示'),
    },
].filter(Boolean);

// 幻灯片列表间隙的右键菜单
export const SlideGapRightMenuItems = [
    // {
    //     id: ActionType.COPY,
    //     title: '复制',
    //     shortcut: 'Cmd+C',
    // },
    {
        id: ActionType.PASTE,
        title: i18n.t('粘贴'),
        shortcut: 'Cmd+V',
    },
    {
        id: ActionType.SELECT_ALL,
        title: i18n.t('全选'),
        shortcut: 'Cmd+A',
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    {
        id: ActionType.CREATE_SLIDE,
        title: i18n.t('新建页面'),
        shortcut: 'Ctrl+M',
    },
    {
        id: ActionType.ADD_SECTION,
        title: i18n.t('添加节'),
    },
    {
        id: ActionType.SHOW_RATIO,
        title: i18n.t('显示比例'),
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    ENABLE_IMPORT_FILE && {
        id: ActionType.IMPORT_SLIDE,
        title: i18n.t('导入幻灯片'),
        children: [
            { id: ActionType.IMPORT_LOCAL_SLIDE, title: i18n.t('从本地文档') },
            ENABLE_IMPORT_ONLINE_SLIDE && { id: ActionType.IMPORT_ONLINE_SLIDE, title: TENANT_CONFIG?.TENANT_DOC_NAME ? `从${TENANT_CONFIG?.TENANT_DOC_NAME}` : i18n.t('从腾讯文档') },
        ].filter(Boolean),
    },
    {
        id: 'divider-menu-item',  // 分割线，所有分割线，都是这个ID
    },
    !ua.isTencentMeeting && {
        id: ActionType.SLIDE_PLAY,
        title: i18n.t('从当前页演示'),
    },
].filter(Boolean);

export const LayoutGapRightMenuItems = [
    {
        id: ActionType.PASTE,
        title: i18n.t('粘贴'),
        shortcut: 'Cmd+V',
    },
    {
        id: 'divider-menu-item',
    },
    {
        id: ActionType.INSERT_LAYOUT,
        title: i18n.t('插入版式'),
    },
];

export const LayoutRightMenuItems = [
    {
        id: ActionType.CUT,
        title: i18n.t('剪切'),
        shortcut: 'Cmd+X',
        disabledContent: i18n.t('已使用的版式无法剪切'),
    },
    ...OnlyCopyRightMenuItems,
    {
        id: ActionType.PASTE,
        title: i18n.t('粘贴'),
        shortcut: 'Cmd+V',
    },
    {
        id: 'divider-menu-item',
    },
    {
        id: ActionType.INSERT_LAYOUT,
        title: i18n.t('插入版式'),
    },
    {
        id: ActionType.COPY_LAYOUT,
        title: i18n.t('复制版式'),
    },
    {
        id: ActionType.DELETE_LAYOUT,
        title: i18n.t('删除版式'),
        disabledContent: i18n.t('已使用的版式无法删除'),
    },
    {
        id: 'divider-menu-item',
    },
    {
        id: ActionType.SET_BG_FORMAT,
        title: i18n.t('设置背景格式'),
    },
];

/**
 * @type {RightMenuItem[]}
 */
export const SlideSectionRightMenuItems = [
    {
        id: ActionType.RENAME_SECTION,
        title: i18n.t('重命名节'),
    },
    {
        id: 'divider-menu-item',
    },
    {
        id: ActionType.DELETE_SECTION,
        title: i18n.t('删除节'),
    },
    {
        id: ActionType.DELETE_SECTION_AND_SLIDES,
        title: i18n.t('删除节和幻灯片'),
    },
    {
        id: ActionType.DELETE_ALL_SECTIONS,
        title: i18n.t('删除所有节'),
    },
    {
        id: 'divider-menu-item',
    },
    {
        id: ActionType.MOVE_SECTION_UP,
        title: i18n.t('上移'),
    },
    {
        id: ActionType.MOVE_SECTION_DOWN,
        title: i18n.t('下移'),
    },
    {
        id: 'divider-menu-item',
    },
    {
        id: ActionType.COLLAPSE_ALL_SECTIONS,
        title: i18n.t('全部折叠'),
    },
    {
        id: ActionType.EXPAND_ALL_SECTIONS,
        title: i18n.t('全部展开'),
    },
];

export const masterDisabledItem = [
    {
        id: ActionType.CUT,
        title: i18n.t('剪切'),
        shortcut: 'Cmd+X',
    },
    ...OnlyCopyRightMenuItems,
    {
        id: ActionType.COPY_LAYOUT,
        title: i18n.t('复制版式'),
    },
    {
        id: ActionType.DELETE_LAYOUT,
        title: i18n.t('删除版式'),
    },
];

export const usedLayoutDisabledItem = [
    {
        id: ActionType.CUT,
        title: i18n.t('剪切'),
        shortcut: 'Cmd+X',
        disabledContent: i18n.t('权限设置无法复制内容'),
    },
    {
        id: ActionType.DELETE_LAYOUT,
        title: i18n.t('删除版式'),
    },
];

function withDivider(items) {
    const filteredItems = items.filter(Boolean);
    if (items.filter(Boolean).length > 0) {
        return [
            { id: 'divider-menu-item' },
            ...filteredItems,
        ];
    }
    return [];
}
